"use client";

import { AudioList } from "@ui/components/core/Audio";
import { Button, buttonVariants } from "@ui/components/core/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@ui/components/core/DropdownMenu";
import { useWavesurfer } from "@ui/hooks/useWavesurferPlayer";
import Pause20 from "@ui/icons/20x20/pause-20.svg";
import Play20 from "@ui/icons/20x20/play-20.svg";
import { cn } from "@ui/lib/utils";
import { clsx } from "clsx";
import { Copy } from "lucide-react";
import * as m from "motion/react-m";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import type { AdditionalFormat } from "../types";

import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";

import {
  getSttDemoContentAnalyticsProps,
  useSTTActions,
  useSTTGlobalSTT,
  useSTTSelectedTabId,
  useSTTTranscriptions,
  useSTTVariant,
  useTranscription,
} from "../STTStore";
import { useSTTTranslationCtx } from "../STTTranslations";
import { base64ToUint8Array } from "../utils";
import { OpenFileButton } from "./OpenFileButton";
import { assertNever } from "@mw/shared/asserts";

const PlayPauseButton = ({ isPlaying, togglePlayPause }: { isPlaying: boolean; togglePlayPause: () => void }) => {
  const transcription = useTranscription();

  return (
    <Button
      className='w-40 shrink-0 p-0'
      disabled={transcription?.isTranscribing}
      onClick={() => {
        togglePlayPause();
      }}
    >
      <m.span animate={{ opacity: 1, scale: 1 }} initial={{ opacity: 0, scale: 0.8 }}>
        {isPlaying ? <Pause20 className='color-white' /> : <Play20 className='color-white' />}
      </m.span>
    </Button>
  );
};

const CopyTranscriptButton = () => {
  const transcription = useTranscription();
  const t = useSTTTranslationCtx();

  const copyTranscript = useCallback(() => {
    if (transcription?.segments) {
      const transcriptText = transcription.text;
      navigator.clipboard.writeText(transcriptText);
      toast.success(t("widget.copied_transcript"));
    }
  }, [transcription, t]);

  return (
    <Button className='w-40 p-0' disabled={transcription?.isTranscribing} onClick={copyTranscript} variant='secondary'>
      <m.span animate={{ opacity: 1, scale: 1 }} initial={{ opacity: 0, scale: 0.8 }}>
        <Copy className='color-white size-16' />
      </m.span>
    </Button>
  );
};

const DownloadDropdown = () => {
  const transcription = useTranscription();
  const selectedTabId = useSTTSelectedTabId();
  const transcriptions = useSTTTranscriptions();
  const globalSTT = useSTTGlobalSTT();
  const t = useSTTTranslationCtx();

  const formatNames = useMemo(() => {
    return {
      txt: t("widget.formats.txt"),
      html: t("widget.formats.html"),
      segmented_json: t("widget.formats.segmented_json"),
      srt: t("widget.formats.srt"),
      pdf: t("widget.formats.pdf"),
      docx: t("widget.formats.docx"),
    } as const;
  }, [t]);

  const downloadFormat = useCallback(
    (format: AdditionalFormat) => {
      if (transcription && format && format.requested_format) {
        // Handle different format downloads
        let content: Uint8Array | string = "";
        const filename = `transcript.${format.file_extension}`;
        const mimeType = format.content_type;

        switch (format.requested_format) {
          case "txt":
            content = format.content;
            break;
          case "html":
            content = format.content;
            break;
          case "srt":
            content = format.content;
            break;
          case "segmented_json":
            content = format.content;
            break;
          case "docx":
            format.is_base64_encoded = true; // API bug workaround
            content = format.is_base64_encoded ? base64ToUint8Array(format.content) : format.content;
            break;
          case "pdf":
            format.is_base64_encoded = true; // API bug workaround
            content = format.is_base64_encoded ? base64ToUint8Array(format.content) : format.content;
            break;
          default: {
            throw assertNever("STT Format, unknown format", format.requested_format);
          }
        }

        // Create and download the file
        const blob = new Blob([content as BlobPart], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();

        // Delay revoking the URL to ensure the download starts before the blob is released
        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }, 1000);

        toast.success(t("widget.download_successful"));

        const contentProps = getSttDemoContentAnalyticsProps({ selectedTabId, transcriptions, globalSTT });
        if (contentProps) {
          captureEvent(EventName.mw_stt_transcription_download, {
            ...contentProps,
            download_format: format.requested_format,
          });
        }
      }
    },
    [transcription, t, selectedTabId, transcriptions, globalSTT],
  );

  // Check if there are any additional formats available
  const hasFormats = transcription?.additional_formats && transcription.additional_formats.length > 0;

  if (!hasFormats) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          "border-none outline-none ring-0",
          buttonVariants({
            variant: "primary",
          }),
        )}
        disabled={transcription?.isTranscribing}
      >
        {t("widget.download")}
      </DropdownMenuTrigger>
      <DropdownMenuContent variant='select'>
        {transcription?.additional_formats?.map((format: AdditionalFormat) => {
          if (!format) {
            return null;
          }
          const formatName = formatNames[format.requested_format] || format.requested_format.toUpperCase();

          return (
            <DropdownMenuItem key={format.requested_format} variant='select' onSelect={() => downloadFormat(format)}>
              <span className='f-ui-06'>
                {formatName} (.{format.file_extension})
              </span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

function STTAudioPlayerContent({ openFileDialog, uri }: { openFileDialog: () => void; uri: string }) {
  const variant = useSTTVariant();
  const timelineRef = useRef<HTMLDivElement>(null);
  const [waveformVisible, setWaveformVisible] = useState(false);

  const onWaveformRender = useCallback(() => {
    setWaveformVisible(true);
  }, []);

  const { mediaElement, isPlaying, togglePlayPause } = useWavesurfer({
    url: uri,
    timelineRef,
    id: uri,
    onRender: onWaveformRender,
  });

  // Sync media element to store for transcript time tracking
  const { setMediaElement } = useSTTActions();
  useEffect(() => {
    if (mediaElement) {
      setMediaElement(mediaElement);
    }
    return () => {
      setMediaElement(null);
    };
  }, [mediaElement, setMediaElement]);

  return (
    <>
      <PlayPauseButton isPlaying={isPlaying} togglePlayPause={togglePlayPause} />
      {/* Waveform display */}
      <div className='flex h-32 min-w-80 flex-grow'>
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
        <div
          className={clsx("w-full transition-opacity duration-300", !waveformVisible && "opacity-0")}
          onClick={event => event.stopPropagation()}
          ref={timelineRef}
        />
      </div>
      {variant === "default" && (
        <>
          <DownloadDropdown />
          <CopyTranscriptButton />
        </>
      )}
      {variant === "simple" && (
        <>
          <OpenFileButton className='ml-16 lg:ml-32' openFileDialog={openFileDialog} size='md' variant='elevated' />
        </>
      )}
    </>
  );
}

export default function STTAudioPlayer({ openFileDialog, uri }: { openFileDialog: () => void; uri: string }) {
  const variant = useSTTVariant();

  return (
    <AudioList
      className={cn(
        "flex items-center gap-8 divide-none border-none p-8 lg:p-16",
        variant === "simple" ? "" : "rounded-b-md rounded-t-inxl bg-default shadow-xs",
      )}
    >
      <STTAudioPlayerContent openFileDialog={openFileDialog} uri={uri} />
    </AudioList>
  );
}
